import { createContext, ReactNode, useCallback, useContext, useMemo, useRef, useState } from 'react'
import { LayerMain, LayerProvider } from '@cargomart/ui'
import { lockScroll, unlockScroll } from 'helpers/scroll'
import noop from 'lodash/noop'

type Modal = { id: `${MODAL_IDS}` }

type ModalContextType = {
  closeModal: (modalId: `${MODAL_IDS}`) => void
  modals: Modal[]
  openModal: (modalId: `${MODAL_IDS}`) => void
}

// TODO поменять на {type: {key: access}}
export enum MODAL_IDS {
  AddCustomerDeparture = 'addCustomerDeparture',
  AddCustomerDestination = 'addCustomerDestination',
  AddCustomerList = 'addCustomerList',
  AddCustomerOrderCreateCustomer = 'addCustomerOrderCreateCustomer',
  AddCustomerPage = 'addCustomerPage',
  AttachContact = 'attachContact',
  CancelOrder = 'cancelOrder',
  CancelOrderList = 'cancelOrderList',
  ContactCreatePage = 'contactCreatePage',
  ContactDetail = 'contactDetail',
  CreateOrderForm = 'createOrderForm',
  DeleteContact = 'deleteContact',
  DeleteContactDetail = 'deleteContactDetail',
  DeleteCustomerDetail = 'deleteCustomerDetail',
  DeleteCustomerList = 'deleteCustomerList',
  DetailCustomer = 'detailCustomer',
  DetailOrder = 'detailOrder',
  DocumentCreate = 'documentCreate',
  IdentificationDocumentsDeparture = 'identificationDocumentsDeparture',
  IdentificationDocumentsDestination = 'identificationDocumentsDestination',
  IdentificationDocumentsDetail = 'identificationDocumentsDetail',
  ModalUserInfoOrderCreate = 'modalUserInfoOrderCreate',
  OrderCreateAddContactDeparture = 'orderCreateAddContactDeparture',
  OrderCreateAddContactDestination = 'orderCreateAddContactDestination',
  OrderCreateAddContactList = 'orderCreateAddContactList',
  OrderCreateAddCustomer = 'orderCreateAddCustomer',
  OrderCreateContactDeparture = 'orderCreateContactDeparture',
  OrderCreateContactDestination = 'orderCreateContactDestination',
  OrderCreateNoDadataRouteDeparture = 'orderCreateNoDadataRouteDeparture',
  OrderCreateNoDadataRouteDestination = 'orderCreateNoDadataRouteDestination',
  OrderCreateOperationPointDeparture = 'OrderCreateOperationPointDeparture',
  OrderCreateOperationPointDestination = 'OrderCreateOperationPointDestination',
  OrderCreateTerminal = 'orderCreateTerminal',
  OrderCreateTerminalDeparture = 'orderCreateTerminalDeparture',
  OrderCreateTerminalDestination = 'orderCreateTerminalDestination',
  OrderCreateTerminalDetail = 'orderCreateTerminalDetail',
  OrderCreateTerminalFromDate = 'orderCreateTerminalFromDate',
  OrderFilterTable = 'orderFilterTable',
  OrderGuestConfirm = 'orderGuestConfirm',
  OrderGuestPartnerCreateDeparture = 'orderGuestPartnerCreateDeparture',
  OrderGuestPartnerCreateDestination = 'orderGuestPartnerCreateDestination',
  OrderServiceTypeInfoCreate = 'orderServiceTypeInfoCreate',
  OrderServiceTypeInfoDetail = 'orderServiceTypeInfoDetail',
  OrderYarcheCreate = 'orderYarcheCreate',
  OrderYarcheDetail = 'orderYarcheDetail',
  OrderYarcheGuestConfirm = 'orderYarcheGuestConfirm',
  OrderYarcheGuestCreate = 'orderYarcheGuestCreate',
  UpdateContact = 'updateContact',
  UserNotification = 'userNotificationModal',
  YarcheDepartureSelectMap = 'yarcheDepartureSelectMap',
  YarcheDepartureTerminal = 'yarcheDepartureTerminal',
  YarcheDestionationSelectMap = 'yarcheDestionationSelectMap',
  YarcheDestionationTerminal = 'yarcheDestionationTerminal'
}

interface ModalProviderProps {
  children: ReactNode
}

const ModalContext = createContext<ModalContextType>({
  closeModal: noop,
  modals: [],
  openModal: noop
})

const ModalProvider = ({ children }: ModalProviderProps) => {
  const [modals, setModalsF] = useState<Modal[]>([])
  const lastFocusElementRef = useRef<Element | null>()

  const openModal = useCallback(
    (modalId: `${MODAL_IDS}`) => {
      if (!modals.some((modal) => modal.id === modalId)) {
        lastFocusElementRef.current = document.activeElement

        lockScroll()
        setModalsF((prevModals) => [
          ...prevModals,
          {
            id: modalId
          }
        ])
      }
    },
    [modals]
  )
  const closeModal = useCallback((modalId: `${MODAL_IDS}`) => {
    if (!modalId) {
      return
    }
    setModalsF((prevModals) => {
      const newModals = prevModals.filter((modal) => modal.id !== modalId)
      if (newModals.length === 0) {
        unlockScroll()
      }

      return newModals
    })

    requestAnimationFrame(() => {
      lastFocusElementRef.current &&
        'focus' in lastFocusElementRef.current &&
        (lastFocusElementRef.current as HTMLElement).focus()
    })
  }, [])

  const contextValue = useMemo(
    () => ({
      modals,
      openModal,
      closeModal
    }),
    [closeModal, modals, openModal]
  )

  return (
    <ModalContext.Provider value={contextValue}>
      <LayerProvider>
        <LayerMain>{children}</LayerMain>
        <div id="MODAL_PORTAL_CONTAINER" />
      </LayerProvider>
    </ModalContext.Provider>
  )
}

const useModal = () => useContext(ModalContext)

export { ModalProvider, useModal }
